import type { User } from "firebase/auth"
import { createContext, type FC, type ReactNode, useEffect, useState } from "react"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { ForbiddenError } from "@/lib/errors"
import { auth } from "@/lib/firebase"
import { useErrorHandler } from "@/lib/useErrorHandler"

const ADMIN_ROLE = "admin"
const isAdmin = (role: unknown) => role === ADMIN_ROLE

const TENANT_ROLE = "tenant"
const isTenant = (role: unknown) => role === TENANT_ROLE

// FIXME: this doesn't make sense anymore. Try to review how to manage authenticated user
const isAdminOrTenant = (role: unknown) => role === undefined || isAdmin(role) || isTenant(role)

export type CurrentUser = User

export const CurrentUserContext = createContext<CurrentUser | null>(null)

interface Props {
  children?: ReactNode
}

export const CurrentUserProvider: FC<Props> = ({ children }) => {
  const { handleError } = useErrorHandler()
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>()

  useEffect(() => {
    return auth.onAuthStateChanged((authUser) =>
      authUser
        ? authUser
            ?.getIdTokenResult()
            .then((idTokenResult) => {
              if (!isAdminOrTenant(idTokenResult.claims.role)) {
                throw new ForbiddenError()
              }
              return authUser
            })
            .then(setCurrentUser)
            .catch((e) =>
              auth
                .signOut()
                .then(() => setCurrentUser(null))
                .then(() => handleError(e))
            )
        : setCurrentUser(null)
    )
  }, [currentUser, handleError])

  if (currentUser === undefined) {
    return (
      <div className="h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return <CurrentUserContext.Provider value={currentUser}>{children}</CurrentUserContext.Provider>
}
